/**
 * Browser-side notifications (cloud web app). The native app routes
 * completion chimes + OS notifications through POST /api/desktop/notify —
 * meaningless in a browser (on cloud it would "play" inside the server
 * container). Here: a synthesized WebAudio chime (no asset, works offline)
 * plus a Web Notification when the tab is hidden.
 *
 * Autoplay policy: an AudioContext starts "suspended" until a user gesture.
 * We resume it on the first pointer/key interaction, so by the time an agent
 * turn completes the chime is allowed. Notification permission is likewise
 * requested on that first gesture (requesting without one is ignored or
 * auto-denied by modern browsers).
 */

let ctx: AudioContext | null = null;
let unlocked = false;

function ensureUnlockListeners(): void {
  const unlock = () => {
    if (!ctx) ctx = new AudioContext();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    unlocked = true;
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock, { passive: true });
  window.addEventListener("keydown", unlock, { passive: true });
}
if (typeof window !== "undefined") ensureUnlockListeners();

/** Two-tone completion chime (~0.35s). Error intent drops to a low buzz. */
export function playChime(intent?: string): void {
  if (!ctx || !unlocked) return; // no gesture yet — silent, never throws
  try {
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const t0 = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.12, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
    gain.connect(ctx.destination);
    const tones = intent === "error" ? [220, 174] : [660, 880];
    tones.forEach((freq, i) => {
      const osc = ctx!.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      osc.start(t0 + i * 0.12);
      osc.stop(t0 + i * 0.12 + 0.22);
    });
  } catch {
    /* audio is best-effort */
  }
}

export interface WebNotifyOptions {
  title: string;
  message: string;
  playSound?: boolean;
  intent?: string;
  onClick?: () => void;
}

/** Chime always (when allowed); a Web Notification only when the tab is
 *  hidden — a visible tab already shows the in-app toast. */
export function webNotify(opts: WebNotifyOptions): void {
  if (opts.playSound !== false) playChime(opts.intent);
  if (
    document.visibilityState === "hidden" &&
    "Notification" in window &&
    Notification.permission === "granted"
  ) {
    try {
      const n = new Notification(opts.title, { body: opts.message, tag: "omniwerk" });
      if (opts.onClick) {
        n.onclick = () => {
          window.focus();
          opts.onClick?.();
          n.close();
        };
      }
    } catch {
      /* notification is best-effort */
    }
  }
}
